/**
 * @copyright Copyright (c) 2016, Pacific Biosciences of California, Inc.
 * @author <a href="mailto:dbarreto@pacificbiosciences.com">David Barreto</a>
 */

import { Report, isReportMissing, BaseReport, MISSING_NAME } from "../model/report-model"
import { apiCache } from "./api-cache"
import { ReportViewRule, ReportRuleAttribute } from "../model/report-view-rules.model"
import { BaseDataset } from "../model/dataset-model"
import * as API from "../api"
import * as datastoreService from "./datastore-service"
import { JobReport } from "../model/job-model"
import { UiPlot, UiReport, UiPlotGroup } from "../model/ui-report-model"
import { DatasetType } from "../model/dataset-type-model"

export interface ReportNamesMap {
    [key: string]: string
}

export interface ReportFormatRule {
    type?: string
    format?: string
    isHidden?: boolean,
    name?: string,
    description?: string
}

export interface ReportFormatMap {
    [key: string]: ReportFormatRule
}

export const reportFormatMapKey = (rule: ReportViewRule, attribute: ReportRuleAttribute) =>
    rule.id + "." + attribute.id

export const getReports = (jobId: number): Promise<UiReport[]> => {
    return getReportNames().then( reportNames => {
        const reportsPromise = API.nJobReports(jobId).then( response => response.data)
        const reports$ = processReports(reportsPromise, reportNames, jobId, true, false)
        return reports$
    })
}

export const getReportNames = (): Promise<ReportNamesMap> => {

    return apiCache.getReportViewRules().then( (rules: ReportViewRule[]) => {
            const namesMap: ReportNamesMap = {}
            if (rules) {
                rules.forEach(rule => {
                    const id = rule.id
                    if (!id) { throw new Error("rule.id is undefined for: " + rule) }
                    namesMap[id] = rule.title || ""
                })
            }
            return namesMap
        })
}

export const getReportRules = (): Promise<ReportFormatMap> => {

    return apiCache.getReportViewRules()
        .then((rules: ReportViewRule[]) => {
            const ruleMap: ReportFormatMap = {}
            if (rules) {
                rules.forEach(rule => {
                    if(rule.attributes !== undefined) {
                        rule.attributes.forEach(attribute => {
                            let formatRule: ReportFormatRule = {}
                            formatRule.type = attribute.type
                            formatRule.format = attribute.format
                            formatRule.isHidden = attribute.isHidden
                            formatRule.description = attribute.description
                            formatRule.name = attribute.name
                            ruleMap[reportFormatMapKey(rule, attribute)] = formatRule
                        })
                    }
                    if(rule.tables !== undefined) {
                        rule.tables.forEach(table => {
                            if(table.columns !== undefined) {
                                table.columns.forEach(column => {
                                    let formatRule: ReportFormatRule = {}
                                    formatRule.type = column.type
                                    formatRule.format = column.format
                                    formatRule.description = column.description
                                    ruleMap[rule.id + "." + table.id + "." + column.id] = formatRule
                                })
                            }
                        })
                    }
                })
            }
            return ruleMap
        })
}


export const getDatasetReports = (dataset: BaseDataset, reportNames: ReportNamesMap, dataType: string): Promise<UiReport[]> => {
    if (typeof dataset.jobId === "undefined") {
        throw new Error("Can't retrieve reports for a dataset with no jobId.  uuid = " + dataset.uuid)
    }
    const excludedTypes = [DatasetType.BARCODE.shortName, DatasetType.REFERENCE.shortName ]
    if (dataType && excludedTypes.indexOf(dataType) >= 0) {
        return Promise.resolve([])
    }
    const reportsPromise = API.anyDatasetRptsByUuid(dataset.uuid, dataType).then( response => response.data)
    return processReports(reportsPromise, reportNames, dataset.jobId as number, false, false).then( reports => {
        return reports.filter( report => { return !isReportMissing(report)})
    })
}

export const getJobReports = (jobId: number, reportNames: ReportNamesMap): Promise<UiReport[]> => {

    const reportsPromise = API.nJobReports(jobId).then( response => response.data)
    return processReports(reportsPromise, reportNames, jobId, true, false)
}

// XXX this is terrible, we need a way to encode this in the data model
// these reports are not shown in SMRT Analysis because they represent
// demultiplexed children, not the overall job
const hiddenReports = new Set<string>([
    "pbreports.tasks.subread_stats",
    "pbreports.tasks.ccs_demux_stats",
    "import_dataset.report_ccs_demux_stats",
    "pb_demultiplex.report_ccs_demux_stats",
    "pb_redo_demux.report_ccs_demux_stats",
])

const processReports = (
    reportsPromise: Promise<any>,
    reportNames: ReportNamesMap,
    jobId: number,
    analysisSilo = false,
    showHiddenFiles = false): Promise<UiReport[]> => {
    const jobReports$ = reportsPromise

    const reportViewRules$ = apiCache.getReportViewRules()

    return reportViewRules$.then((reportViewRules: ReportViewRule[]) => {

        const reportViewRulesMap = new Map()
        reportViewRules = reportViewRules || []

        for (let rule of reportViewRules) {
            reportViewRulesMap.set(rule.id || "", rule)
        }

        return jobReports$
            .then((jobReports: JobReport[]) => {
                jobReports = jobReports || []
                if (!showHiddenFiles) {
                    jobReports = jobReports.filter(jobReport => {
                        let id = jobReport.reportTypeId || ""
                        let endOfPrefixPosition = id.lastIndexOf(".")
                        if (endOfPrefixPosition > -1) { id = id.substring(endOfPrefixPosition + 1).replace("report_", "") }
                        const rule = reportViewRulesMap.get(id)
                        return !rule || !rule.isHidden
                    })
                }

                if (!jobReports.length) {
                    return []
                }

                /* eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars */
                let reportsFilter = function(jobReport) {
                    return true
                }
                // filter out special-case reports such as the child dataset
                // demux statistics
                if (analysisSilo) {
                    reportsFilter = function(jobReport) {
                        return !hiddenReports.has(jobReport.reportTypeId)
                    }
                }

                const reports = jobReports.filter(reportsFilter).map(
                    jobReport => {
                    if (jobReport.dataStoreFile) {
                        const uuid = jobReport.dataStoreFile!.uuid || ""
                        return datastoreService.getReportFile(jobId, uuid)
                            .then(serverReport => {
                                const rule: ReportViewRule = serverReport.id ? reportViewRulesMap.get(serverReport.id) : null
                                return processReport(jobId, serverReport, reportNames, rule)
                            })
                    } else {
                        throw new Error("jobReport.dataStoreFile is undefined")
                    }
                })
                return Promise.all(reports)
            })
        })
}

const processReport = (jobId: number, report: Report, reportNames: ReportNamesMap, viewRule: ReportViewRule): Promise<UiReport> => {
    return Promise.resolve(processReportMetadata(jobId, report, reportNames, viewRule))
}

const processReportMetadata =
    (jobId: number, report: Report, reportNamesMap: ReportNamesMap, viewRule?: ReportViewRule): UiReport => {
    report = report || {}
    const uuid = report.uuid
    const label: String = labelForReport(report,reportNamesMap)

    // Add "description" from view rules to ui version of plotGroups

    const plotGroups: UiPlotGroup[] = report.plotGroups ?
        report.plotGroups.map( serverPlotGroup => {
            const plots: UiPlot[] = serverPlotGroup.plots ?
                serverPlotGroup.plots.map(plot => {
                    const imageUrl = uuid && plot.image ?
                        datastoreService.getUrlForImageFile(
                            { jobId, datastoreUuid: uuid, fileName: plot.image }
                        ) :
                        undefined
                    return Object.assign({}, plot, {
                        imageUrl,
                        caption: plot.caption || plot.title
                    }) as UiPlot
                }) :
                []
            const thumbnailUrl = uuid && serverPlotGroup.thumbnail ?
                datastoreService.getUrlForImageFile(
                    { jobId, datastoreUuid: uuid, fileName: serverPlotGroup.thumbnail }
                ) :
                undefined
            let uiPlotGroup: UiPlotGroup = Object.assign({}, serverPlotGroup, { plots, thumbnailUrl })
            if (viewRule && viewRule.plotGroups) {
                let pgRule = viewRule.plotGroups.find( pg =>  {
                    // seams to be some inconsistency here, but he plotGroupId in the viewRule should look either like
                    // "ccs_read_concordance_group" or like "mapping_stats_ccs.ccs_read_concordance_group"
                    const serverPgIds = serverPlotGroup.id.split(".")
                    const plotId = serverPgIds.pop()
                    return pg.id === serverPlotGroup.id || pg.id === plotId
                })
                if (pgRule) {
                    uiPlotGroup.description  = pgRule.description
                }
            }
            return uiPlotGroup
        }) 
        : []

    const plots: UiPlot[] = plotGroups.reduce((allPlots, plotGroup) => {
        return allPlots.concat(plotGroup.plots || [])
    },                               [] as UiPlot[])

    const result: UiReport = Object.assign({}, report, {
        label,
        plots,
        plotGroups: plotGroups
    }) as any

    return result
}

export const processReportMetadataForTest = processReportMetadata

const labelForReport = (report: BaseReport, reportNamesMap: ReportNamesMap): String =>  {
    if (isReportMissing(report)) {
        return `${MISSING_NAME}. (uuid: ${report.uuid})`
    }
    const id = report.id
    if (!id) { throw new Error("report.id is undefined") }
    return reportNamesMap[id] || report.title || id.toString()
}
